// src/hooks/useViewManager.js
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useClaims } from './useClaims';
import { auth } from '../config/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

export const useViewManager = ({
  bookId,
  bookData,
  defaultView = 'public',
  customPermissionCheck = null
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Claims-based permissions (PRIMARY source)
  const { 
    permissions, 
    bookRole, 
    loading: claimsLoading,
    isOwner: isClaimsOwner,
    isBookAdmin 
  } = useClaims(bookId);
  
  const [viewMode, setViewMode] = useState(defaultView);
  const [user, setUser] = useState(null);
  const [hasManagementAccess, setHasManagementAccess] = useState(false);
  const [hasViewAccess, setHasViewAccess] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessDeniedReason, setAccessDeniedReason] = useState(null);

  // Extract access settings from bookData
  const accessLevel = bookData?.accessLevel || 'public';
  const requireAuth = bookData?.requireAuth || false;
  const authorizedUsers = bookData?.authorizedUsers || [];
  const managementAccess = bookData?.managementAccess || {
    allOrgUsers: false,
    specificEmails: []
  };
  const viewSettings = bookData?.albumSettings?.viewSettings || null;
  const baseViewAccess = bookData?.viewAccess || {};

  const viewAccess = {
    enablePublicView: viewSettings?.enablePublicView ?? baseViewAccess.enablePublicView ?? true,
    enableManagementView: viewSettings?.enableManagementView ?? baseViewAccess.enableManagementView ?? true,
    allowViewToggle: baseViewAccess.allowViewToggle ?? true,
    defaultView: viewSettings?.defaultView ?? baseViewAccess.defaultView ?? 'public',
    requireAuthForManagement: baseViewAccess.requireAuthForManagement ?? true,
    publicViewLabel: viewSettings?.publicViewLabel ?? baseViewAccess.publicViewLabel ?? 'Public View',
    managementViewLabel: viewSettings?.managementViewLabel ?? baseViewAccess.managementViewLabel ?? 'Management'
  };
  const organizationId = bookData?.organizationId;

  // Determine management access based on claims FIRST, then fallback to legacy checks
  const determineManagementAccess = useCallback(async (authUser) => {
    if (!authUser || !bookData) return { hasAccess: false, role: null };

    // 1. CLAIMS-BASED CHECK (PRIMARY) - Owner via claims
    if (isClaimsOwner || permissions.isSuperAdmin) {
      return { hasAccess: true, role: 'owner' };
    }

    // 2. CLAIMS-BASED CHECK - Admin/Editor via claims
    if (permissions.canManage) {
      return { hasAccess: true, role: bookRole || 'admin' };
    }

    // 3. CLAIMS-BASED CHECK - Editor can manage
    if (permissions.canEdit) {
      return { hasAccess: true, role: bookRole || 'editor' };
    }

    // 4. LEGACY CHECK - Document ownerId field
    if (bookData.ownerId === authUser.uid) {
      return { hasAccess: true, role: 'owner' };
    }

    // 5. LEGACY CHECK - Specific emails in managementAccess
    if (managementAccess.specificEmails?.includes(authUser.email)) {
      return { hasAccess: true, role: 'manager' };
    }

    // 6. LEGACY CHECK - All org users have access
    if (managementAccess.allOrgUsers && organizationId) {
      const isMember = await checkOrganizationMembership(authUser, organizationId);
      if (isMember) {
        return { hasAccess: true, role: 'org_manager' };
      }
    }

    // 7. LEGACY CHECK - Organization role
    if (organizationId) {
      const orgAccess = await checkOrganizationRole(authUser, organizationId);
      if (orgAccess.hasAccess) {
        return orgAccess;
      }
    }

    // 8. Custom permission check (if provided)
    if (customPermissionCheck) {
      return await customPermissionCheck(authUser, bookData, bookId);
    }

    return { hasAccess: false, role: null };
  }, [bookData, bookId, isClaimsOwner, permissions, bookRole, managementAccess, organizationId, customPermissionCheck]);

  // Determine view access
  const determineViewAccess = useCallback(async (authUser) => {
    if (!bookData) return { hasAccess: false, reason: 'no_book_data' };

    const published = bookData.published !== false;

    // Claims-based: Owner/Admin can always view
    if (isClaimsOwner || permissions.canManage || permissions.isSuperAdmin) {
      return { hasAccess: true, reason: null };
    }

    // Claims-based: Any book role can view
    if (bookRole) {
      return { hasAccess: true, reason: null };
    }

    // Legacy: Document owner can view
    if (authUser && bookData.ownerId === authUser.uid) {
      return { hasAccess: true, reason: null };
    }

    // Unpublished content - only owner/admin can view
    if (!published) {
      return { hasAccess: false, reason: 'unpublished' };
    }

    // Public access
    if (accessLevel === 'public') {
      if (bookData.allowAnonymousViews === false && !authUser) {
        return { hasAccess: false, reason: 'auth_required' };
      }
      return { hasAccess: true, reason: null };
    }

    // Password protected
    if (accessLevel === 'password') {
      return { hasAccess: true, reason: null };
    }

    // Private access
    if (accessLevel === 'private') {
      if (!authUser) {
        return { hasAccess: false, reason: 'auth_required' };
      }

      // Check authorized users list (legacy email-based)
      if (authorizedUsers.includes(authUser.email)) {
        return { hasAccess: true, reason: null };
      }

      // Check if user has management access (they can also view)
      const mgmtAccess = await determineManagementAccess(authUser);
      if (mgmtAccess.hasAccess) {
        return { hasAccess: true, reason: null };
      }

      return { hasAccess: false, reason: 'not_authorized' };
    }

    // Organization access
    if (accessLevel === 'organization') {
      if (!authUser) {
        return { hasAccess: false, reason: 'auth_required' };
      }

      if (organizationId) {
        const isMember = await checkOrganizationMembership(authUser, organizationId);
        if (isMember) {
          return { hasAccess: true, reason: null };
        }
      }

      return { hasAccess: false, reason: 'not_org_member' };
    }

    return { hasAccess: false, reason: 'invalid_access_level' };
  }, [bookData, accessLevel, authorizedUsers, organizationId, isClaimsOwner, permissions, bookRole, determineManagementAccess]);

  // Monitor authentication state
  useEffect(() => {
    // Wait for claims to load
    if (claimsLoading) return;

    const unsubscribe = auth.onAuthStateChanged(async (authUser) => {
      setUser(authUser);
      
      if (bookData) {
        // Check view access
        const viewAccessResult = await determineViewAccess(authUser);
        setHasViewAccess(viewAccessResult.hasAccess);
        
        if (!viewAccessResult.hasAccess) {
          setAccessDeniedReason(viewAccessResult.reason);
          setHasManagementAccess(false);
          setUserRole(null);
          
          if (viewMode === 'management') {
            setViewMode('public');
            updateURL('public');
          }
          setLoading(false);
          return;
        }

        setAccessDeniedReason(null);

        // Check management access
        const mgmtResult = await determineManagementAccess(authUser);
        setHasManagementAccess(mgmtResult.hasAccess);
        setUserRole(mgmtResult.role);
        
        // Redirect if no management access but trying to view management
        if (viewMode === 'management' && !mgmtResult.hasAccess) {
          setViewMode('public');
          updateURL('public');
        }
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, [bookId, bookData, viewMode, claimsLoading, determineViewAccess, determineManagementAccess]);

  // Organization membership check (legacy)
  const checkOrganizationMembership = async (user, orgId) => {
    if (!user || !orgId) return false;

    try {
      const membersRef = collection(db, 'pb-organisation-members');
      const q = query(
        membersRef,
        where('organisationId', '==', orgId),
        where('userId', '==', user.uid),
        where('status', '==', 'active')
      );
      const snapshot = await getDocs(q);
      return !snapshot.empty;
    } catch (error) {
      console.error('Error checking organization membership:', error);
      return false;
    }
  };

  // Organization role check (legacy)
  const checkOrganizationRole = async (user, orgId) => {
    if (!user || !orgId) return { hasAccess: false, role: null };

    try {
      const membersRef = collection(db, 'pb-organisation-members');
      const q = query(
        membersRef,
        where('organisationId', '==', orgId),
        where('userId', '==', user.uid)
      );
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const memberData = snapshot.docs[0].data();
        const managementRoles = ['owner', 'admin', 'manager', 'editor'];
        if (managementRoles.includes(memberData.role)) {
          return { hasAccess: true, role: memberData.role };
        }
      }

      return { hasAccess: false, role: null };
    } catch (error) {
      console.error('Error checking organisation role:', error);
      return { hasAccess: false, role: null };
    }
  };

  // Toggle between views
  const toggleView = useCallback(() => {
    if (viewMode === 'public') {
      if (!viewAccess.enableManagementView) return;

      if (!user && viewAccess.requireAuthForManagement) {
        const returnUrl = `${location.pathname}?view=management`;
        navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
        return;
      }
      
      if (!hasManagementAccess) return;
      
      setViewMode('management');
      updateURL('management');
    } else {
      if (!viewAccess.enablePublicView) return;
      setViewMode('public');
      updateURL('public');
    }
  }, [viewMode, user, hasManagementAccess, viewAccess, navigate, location.pathname]);

  // Set specific view
  const setView = useCallback((mode) => {
    if (!['public', 'management'].includes(mode)) return;
    
    if (mode === 'management') {
      if (!viewAccess.enableManagementView) return;
      if (!user && viewAccess.requireAuthForManagement) {
        const returnUrl = `${location.pathname}?view=management`;
        navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
        return;
      }
      if (!hasManagementAccess) return;
    } else {
      if (!viewAccess.enablePublicView) return;
    }
    
    setViewMode(mode);
    updateURL(mode);
  }, [user, hasManagementAccess, viewAccess, navigate, location.pathname]);

  // Update URL with view parameter
  const updateURL = (mode) => {
    const params = new URLSearchParams(location.search);
    params.set('view', mode);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  // Check if view is available
  const isViewAvailable = (mode) => {
    if (mode === 'public') {
      return viewAccess.enablePublicView && hasViewAccess;
    }
    if (mode === 'management') {
      return viewAccess.enableManagementView && hasManagementAccess;
    }
    return false;
  };

  // Check if view toggle should be shown
  const canToggleView = () => {
    return (
      viewAccess.allowViewToggle &&
      viewAccess.enablePublicView &&
      viewAccess.enableManagementView &&
      hasManagementAccess &&
      hasViewAccess
    );
  };

  return {
    viewMode,
    user,
    isAuthenticated: !!user,
    hasManagementAccess,
    hasViewAccess,
    accessDeniedReason,
    userRole,
    loading: loading || claimsLoading,
    toggleView,
    setView,
    isViewAvailable,
    canToggleView,
    
    // Claims-based permissions (NEW)
    permissions,
    bookRole,
    isOwner: isClaimsOwner || (user && bookData?.ownerId === user.uid),
    isAdmin: isBookAdmin || permissions.isAdmin,
    canEdit: permissions.canEdit,
    canManage: permissions.canManage,
    
    viewConfig: {
      enablePublicView: viewAccess.enablePublicView,
      enableManagementView: viewAccess.enableManagementView,
      allowViewToggle: viewAccess.allowViewToggle,
      defaultView: viewAccess.defaultView,
      requireAuthForManagement: viewAccess.requireAuthForManagement,
      publicViewLabel: viewAccess.publicViewLabel || 'Public View',
      managementViewLabel: viewAccess.managementViewLabel || 'Management'
    },
    accessConfig: {
      accessLevel,
      requireAuth,
      published: bookData?.published !== false,
      allowAnonymousViews: bookData?.allowAnonymousViews !== false
    }
  };
};